import React, {useCallback, useEffect, useState} from 'react';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Paper from '@material-ui/core/Paper';
import {ParticipantModel} from '../../../../../../interface/iParticipant';
import CustomCheckBox from './CustomCheckbox';
import UtilService from '../../../../../../helper/service';
import DotMenu from "./DotMenu";
import Tooltip from '@material-ui/core/Tooltip';
import {useSelector} from "react-redux";
import {useStylesTooltip} from "../../../../../../config/theme";

const loginUserData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || '{}') : "";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (orderBy === 'phone' || orderBy === 'sensor') {
    const bData: any = b;
    const aData: any = a;
    let bname = '';
    let aname = '';
    if (bData.user_devices && bData.user_devices[orderBy]) {
      bname = bData.user_devices[orderBy].toLowerCase()
    }
    if (aData.user_devices && aData.user_devices[orderBy]) {
      aname = aData.user_devices[orderBy].toLowerCase()
    }
    if (bname < aname) {
      return -1;
    }
    if (bname > aname) {
      return 1;
    }
  } else if (orderBy === 'tag_cloud') {
    const bData: any = b[orderBy];
    const aData: any = a[orderBy];
    if (UtilService.getParsedTagsForSort(bData) < UtilService.getParsedTagsForSort(aData)) {
      return -1;
    }
    if (UtilService.getParsedTagsForSort(bData) > UtilService.getParsedTagsForSort(aData)) {
      return 1;
    }
  } else {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  disablePadding?: boolean;
  id?: keyof ParticipantModel;
  label?: string;
  numeric?: boolean;
  color?: string;
  minWidth?: number;
}

const headCells: HeadCell[] = [
  { id: 'participant_id', numeric: false, disablePadding: true, label: '', color: 'primary' },
  { id: 'temp_email', numeric: false, disablePadding: true, label: 'Participant Email', color: 'default' },
  { id: 'participant_status', numeric: false, disablePadding: false, label: 'Status', color: 'default' },
  { id: 'last_step', numeric: false, disablePadding: false, label: 'Last Step', color: 'default', minWidth: 120 },
  { id: 'last_step_at', numeric: false, disablePadding: false, label: 'Time Of Last Step', color: 'default', minWidth: 162 },
  { id: 'phone', numeric: false, disablePadding: false, label: 'Phone', color: 'default' },
  { id: 'sensor', numeric: false, disablePadding: false, label: 'Sensor', color: 'default' },
  { id: 'recommended_action', numeric: false, disablePadding: false, label: 'Recommended Action', color: 'default', minWidth: 187 },
  { id: 'tag_cloud', numeric: false, disablePadding: false, label: 'Tags', color: 'default' },
  { id: 'tower_tag_cloud', numeric: false, disablePadding: false, label: 'Tower Tags', color: 'default', minWidth: 124 },
  { id: 'user_id', numeric: false, disablePadding: false, label: '', color: 'default' },
];

const mobileHeadCells: HeadCell[] = [
  { id: 'temp_email', numeric: false, disablePadding: true, label: 'Participant Email', color: 'default' },
  { id: 'participant_status', numeric: false, disablePadding: false, label: 'Status', color: 'default' }
];

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof ParticipantModel) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  isMobile: boolean;
  recommendTags:boolean
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      paddingBottom: 80
    },
    paper: {
      width: '100%',
      marginBottom: theme.spacing(2),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      borderRadius: 10
    },
    table: {
      minWidth: 750,
      '@media (max-width: 600px)': {
        width: '100%',
        minWidth: 'unset'
      }
    },
    headerText: {
      color: '#212D40',
      fontFamily: 'roboto-bold',
      fontSize: 20,
      marginTop: 27,
      marginLeft: 17,
      '@media (max-width: 374px)': {
        fontSize: 17
      }
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    primaryTxt: {
      fontFamily: 'roboto-bold',
      fontSize: 12,
      textTransform: 'uppercase',
      lineHeight: 1.5,
      '@media (max-width: 1025px)': {
        fontSize: 10
      }
    },
    defaulttxt: {
      fontFamily: 'roboto-bold',
      fontSize: 12,
      color: '#606B76',
      textTransform: 'uppercase',
    },
    progress: {
      borderRadius: 50,
      width: 30,
      height: 30,
      color: '#1DB859',
      verticalAlign: 'middle'
    },
    error: {
      borderRadius: 50,
      width: 30,
      height: 30,
      color: '#ED564A',
      verticalAlign: 'middle'
    },
    completed: {
      borderRadius: 50,
      width: 30,
      height: 30,
      color: '#697683',
      verticalAlign: 'middle'
    },
    notstarted: {
      borderRadius: 50,
      width: 30,
      height: 30,
      color: '#F5B342',
      verticalAlign: 'middle'
    },
    filter: {
      float: "right"
    },
    dropdown: {
      position: 'absolute',
      top: 10,
      minWidth: 100,
      right: 0,
      zIndex: 1,
      padding: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
    },
    statusFilter: {
      minWidth: 120
    },

    tableRowNormal: {
      backgroundColor: '#FFFFFF !important',
    },
    tableRowHover: {
      backgroundColor: '#F2F7FC !important'
    },
    tableRowSelected: {
      backgroundColor: '#F2F7FC !important'
    },
    sensorIcon: {
      width: 15,
      height: 26
    }
  }),
);

type Props = {
  order: string;
};

const sortIconStyles = makeStyles<Theme, Props>(() =>
  createStyles({
    icon: {
      width: 12,
      height: 'auto',
      marginLeft: 5,
      transform: ({ order }) => order === 'desc' ? 'rotate(180deg)' : ''
    }
  }),
);

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    classes,
    order,
    orderBy,
    onRequestSort,
    onSelectAllClick,
    rowCount,
    numSelected,
    isMobile,
    recommendTags
  } = props;

  const createSortHandler = (property: keyof ParticipantModel) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  const CustomSortIconComponent = useCallback((order: string, orderBy: string, id: any) => {
    const classes = sortIconStyles({ order });
    const tooltipClasses = useStylesTooltip();
    return (
      orderBy === id ? (
        <Tooltip classes={tooltipClasses} title="Sort Participants" placement="bottom" arrow>
          <img className={classes.icon} src={'/assets/icons/sort.svg'} alt='sort icon' />
        </Tooltip>
      ) : (<></>)
    )
  }, [order, orderBy]);

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          {!isMobile && <CustomCheckBox
            isheader={'true'}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />}
        </TableCell>
        {isMobile ? (
          mobileHeadCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              align="left"
              padding="normal"
              sortDirection={orderBy === headCell.id ? order : false}
              className={classes.primaryTxt}
            >
              {(headCell.id === 'participant_id'
                || headCell.id === 'temp_email'
                || headCell.id === 'participant_status') ? (
                <TableSortLabel
                  style={{ color: orderBy === headCell.id ? '#017AEF' : '' }}
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                  IconComponent={() => CustomSortIconComponent(order, orderBy, headCell.id)}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              ) : (
                <span>{headCell.label}</span>
              )}
            </TableCell>
          ))
        ) : (
          headCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              align="left"
              padding="normal"
              sortDirection={orderBy === headCell.id ? order : false}
              className={classes.primaryTxt}
              style={headCell.minWidth ? { minWidth: headCell.minWidth } : {}}
            >
              {(headCell.id === 'participant_id'
                || headCell.id === 'temp_email'
                || headCell.id === 'participant_status'
                || headCell.id === 'last_step'
                || headCell.id === 'last_step_at'
                || headCell.id === 'phone'
                || headCell.id === 'sensor'
                || headCell.id === 'recommended_action'
                || headCell.id === 'tag_cloud'
                || headCell.id === 'tower_tag_cloud'
                || headCell.id === 'user_id') ? (
                <TableSortLabel
                  style={{ color: orderBy === headCell.id ? '#017AEF' : '', }}
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                  IconComponent={() => CustomSortIconComponent(order, orderBy, headCell.id)}
                >
                  {headCell.id == 'tower_tag_cloud' ? recommendTags && headCell.label : headCell.label}
                  {orderBy === headCell.id ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              ) : (
                <span>{headCell.label}</span>
              )}
            </TableCell>
          ))
        )}
      </TableRow>
    </TableHead>
  );
}

type PropsFunction = (ParticipantModel: string[]) => void

const EnhancedTable = React.forwardRef(
  (
    { rows, onSelectionChange, dotEditTags, dotRemoveParticipant, isMobile }: { rows: any[], onSelectionChange: PropsFunction, dotEditTags: any, dotRemoveParticipant: any, isMobile: boolean },
    ref: React.Ref<any>
  ) => {
    EnhancedTable.displayName = 'participant';

    const classes = useStyles();
    const tooltipClasses = useStylesTooltip();
    const { tagApplied } = useSelector((state: any) => state.creatorExperienceManager);
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<keyof ParticipantModel>('temp_email');
    const [selected, setSelected] = useState<string[]>([]);
    const [hoverParticipantId, setHoverParticipantId] = useState('');
    const [recommendTags, setRecommendTagsRole] = useState(false);

    useEffect(() => {
      if (loginUserData && loginUserData.role_access) {
        if (loginUserData.role_access.length) {
          const recommend_tags = loginUserData.role_access.find((val: any) => val.access_name === "recommend_tags");
          if (recommend_tags && recommend_tags.access_value) {
            setRecommendTagsRole(true)
          }
        }
      }
    }, []);

    React.useImperativeHandle(
      ref,
      () => ({
        clearAllSelRows() {
          setSelected([]);
        }
      })
    );

    useEffect(() => {
      if (tagApplied) {
        setSelected([]);
        onSelectionChange([]);
      }
    }, [tagApplied]);

    const handleRequestSort = ({ }, property: keyof ParticipantModel) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newSelecteds = rows.map((n) => n.participant_id);
        setSelected(newSelecteds);
        onSelectionChange(newSelecteds);
        return;
      }
      setSelected([]);
      onSelectionChange([]);
    };

    const handleClick = ({ }, id: string) => {
      const selectedIndex = selected.indexOf(id);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1),
        );
      }
      setSelected(newSelected);
      onSelectionChange(newSelected)
    };

    const isSelected = (id: string) => selected.indexOf(id) !== -1;
    const isHover = (id: string) => hoverParticipantId === id;

    useEffect(() => {
      const allStatus: any = [];
      rows.map((item) => {
        if (item.participant_status !== "" && item.participant_status !== null) {
          allStatus.push({ id: item.participant_status, label: UtilService.participantStatus(item.participant_status) })
        }
      })
    }, []);

    return (
      <div className={classes.root}>

        <Paper className={classes.paper}>

          <TableContainer>
            <div className={classes.headerText}>Participant Device Connectivity</div>

            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                classes={classes}
                isMobile={isMobile}
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
				        recommendTags={recommendTags}
              />
              <TableBody>
                {stableSort(rows, getComparator(order, orderBy))
                  .map((row: any, index) => {
                    const isItemSelected = isSelected(row.participant_id);
                    const isItemHover = isHover(row.participant_id);
                    const labelId = `enhanced-table-checkbox-${index}`;
                    const user_devices: any = row.user_devices ? row.user_devices : {}
                //**************Hotfix *********** Changing sensors naming without changing the mobile app****************
                    if (user_devices.sensor == 'Watch6,10'){
                      user_devices.sensor = 'Apple Watch SE 2';
                    } else if (user_devices.sensor == 'Watch6,11'){
                      user_devices.sensor = 'Apple Watch SE 2';
                    } else if (user_devices.sensor == 'Watch6,12'){
                      user_devices.sensor = 'Apple Watch SE 2';
                    } else if (user_devices.sensor == 'Watch6,13'){
                      user_devices.sensor = 'Apple Watch SE 2';
                    } else if (user_devices.sensor == 'Watch6,14'){
                      user_devices.sensor = 'Apple Watch Series 8';
                    } else if (user_devices.sensor == 'Watch6,15'){
                      user_devices.sensor = 'Apple Watch Series 8';
                    } else if (user_devices.sensor == 'Watch6,16'){
                      user_devices.sensor = 'Apple Watch Series 8';
                    } else if (user_devices.sensor == 'Watch6,17'){
                      user_devices.sensor = 'Apple Watch Series 8';
                    } else if (user_devices.sensor == 'Watch6,18'){
                      user_devices.sensor = 'Apple Watch Ultra';
                    } else if (user_devices.sensor == 'Watch7,1'){//new
                      user_devices.sensor = 'Apple Watch Series 9';
                    } else if (user_devices.sensor == 'Watch7,2'){//new
                      user_devices.sensor = 'Apple Watch Series 9';
                    } else if (user_devices.sensor == 'Watch7,3'){//new
                      user_devices.sensor = 'Apple Watch Series 9';
                    } else if (user_devices.sensor == 'Watch7,4'){//new
                      user_devices.sensor = 'Apple Watch Series 9';
                    } else if (user_devices.sensor == 'Watch7,5'){//new
                      user_devices.sensor = 'Apple Watch Ultra 2';
                    } else if (user_devices.sensor == 'Watch7,8'){//new
                      user_devices.sensor = 'Apple Watch Series 10';
                    } else if (user_devices.sensor == 'Watch7,9'){//new
                      user_devices.sensor = 'Apple Watch Series 10';
                    } else if (user_devices.sensor == 'Watch7,10'){//new
                      user_devices.sensor = 'Apple Watch Series 10';
                    } else if (user_devices.sensor == 'Watch7,11'){//new
                      user_devices.sensor = 'Apple Watch Series 10';
                    }
                //*********************************************************************************************************
                    return (
                      <TableRow
                        onMouseOver={() => setHoverParticipantId(row.participant_id)}
                        onMouseLeave={() => setHoverParticipantId('')}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.participant_id}
                        selected={isItemSelected}
                        hover={isItemHover}
                        classes={{
                          root: classes.tableRowNormal,
                          hover: classes.tableRowHover,
                          selected: classes.tableRowSelected,
                        }}
                      >
                        {isMobile ? (
                          <>
                            <TableCell padding="none" size="small" align="center">
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {row.indicator === "GREEN" && (<img src={'/assets/icons/participant/watch-green.svg'} className={classes.sensorIcon} alt='watch-green' />)}
                                {(row.indicator === 'RED' || !row.indicator) && (<img src={'/assets/icons/participant/watch-red.svg'} className={classes.sensorIcon} alt='watch-red' />)}
                                {row.indicator === 'YELLOW' && (<img src={'/assets/icons/participant/watch-yellow.svg'} className={classes.sensorIcon} alt='watch-yellow' />)}
                              </div>
                            </TableCell>
                            <TableCell
                              id={labelId}
                              scope="row"
                              align="left"
                              style={{ maxWidth: '50vw', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                            >
                              {row.temp_email}
                            </TableCell>
                            <TableCell align="left">{UtilService.participantStatus(row.participant_status)}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell padding="checkbox">
                              {(hoverParticipantId === row.participant_id || isItemSelected) && (

                                <CustomCheckBox
                                  key={row.participant_id}
                                  checked={isItemSelected}
                                  onClick={(event: any) => handleClick(event, row.participant_id)}
                                  inputProps={{ 'aria-labelledby': labelId }}
                                />
                              )}
                            </TableCell>
                            <TableCell padding="none" size="small" align="center">
                              <Tooltip classes={tooltipClasses} title="Shows green when device is successfully collecting data" placement="bottom" arrow>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {row.indicator === "GREEN" && (<img src={'/assets/icons/participant/watch-green.svg'} className={classes.sensorIcon} alt='watch-green' />)}
                                  {(row.indicator === 'RED' || !row.indicator) && (<img src={'/assets/icons/participant/watch-red.svg'} className={classes.sensorIcon} alt='watch-red' />)}
                                  {row.indicator === 'YELLOW' && (<img src={'/assets/icons/participant/watch-yellow.svg'} className={classes.sensorIcon} alt='watch-yellow' />)}
                                </div>
                              </Tooltip>
                            </TableCell>
                            <TableCell id={labelId} scope="row" align="left">{row.temp_email}</TableCell>
                            <TableCell align="left">{UtilService.participantStatus(row.participant_status)}</TableCell>
                            <TableCell align="left">{UtilService.formattingString(row.last_step)}</TableCell>
                            <TableCell align="left">{(row.last_step === 'ADDED' || row.last_step === 'NEW_USER' || row.last_step === 'NO_SENSOR_REGISTERED') ? "-" : UtilService.getParticipantDateFormatString(row.last_step_at)}</TableCell>
                            <TableCell align="left">{user_devices.phone ? <Tooltip title={user_devices.phone_type} placement="top" arrow={true}><span>{user_devices.phone}</span></Tooltip> : "-"}</TableCell>
                            <TableCell align="left">{user_devices.sensor ? <Tooltip title={user_devices.sensor_type} placement="top" arrow={true}><span>{user_devices.sensor}</span></Tooltip> : "-"}</TableCell>
                            <TableCell align="left">{row.recommended_action || '-'}</TableCell>
                            <TableCell align="left">{row.tag_cloud ? UtilService.getParsedTags(row.tag_cloud) : '-'}</TableCell>
                            {recommendTags && <TableCell align="left">{row.tower_tag_cloud ?UtilService.getParsedTowerTags( row.tower_tag_cloud) : '-'}</TableCell>}
                            <TableCell align="right" padding='none'>
                              {(hoverParticipantId === row.participant_id && !isItemSelected) && (
                                <DotMenu
                                  key={row.participant_id}
                                  editTag={() => dotEditTags(row)}
                                  removeParticipant={() => dotRemoveParticipant(row)}
                                />
                              )}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

        </Paper>
      </div>
    );
  });

export default EnhancedTable;